import * as THREE from 'three';

// Truncate an icosahedron into the 12 pentagons and 20 hexagons of a football.
export function buildFootballGeometry(radius: number): THREE.BufferGeometry {
  const ico = new THREE.IcosahedronGeometry(1, 0);
  const source = ico.getAttribute('position');
  const vertices: THREE.Vector3[] = [];
  const faces: number[][] = [];
  for (let i = 0; i < source.count; i += 3) {
    const face: number[] = [];
    for (let j = 0; j < 3; j++) {
      const v = new THREE.Vector3().fromBufferAttribute(source, i + j);
      let index = vertices.findIndex(other => other.distanceToSquared(v) < 1e-8);
      if (index < 0) { index = vertices.length; vertices.push(v); }
      face.push(index);
    }
    faces.push(face);
  }
  ico.dispose();
  const positions: number[] = [], colors: number[] = [];
  const seam = new THREE.Color('#424b51');
  const pale = new THREE.Color('#fff9e8');
  const black = new THREE.Color('#17252f');
  const triangle = (a: THREE.Vector3, b: THREE.Vector3, c: THREE.Vector3, color: THREE.Color) => {
    for (const point of [a, b, c]) { positions.push(point.x, point.y, point.z); colors.push(color.r, color.g, color.b); }
  };
  const panel = (points: THREE.Vector3[], color: THREE.Color) => {
    const center = points.reduce((sum, point) => sum.add(point), new THREE.Vector3()).divideScalar(points.length);
    const normal = center.clone().normalize();
    const axis = points[0]!.clone().sub(center).normalize();
    const tangent = new THREE.Vector3().crossVectors(normal, axis);
    points.sort((a, b) => Math.atan2(a.clone().sub(center).dot(tangent), a.clone().sub(center).dot(axis)) - Math.atan2(b.clone().sub(center).dot(tangent), b.clone().sub(center).dot(axis)));
    const outer = points.map(p => p.clone().lerp(center, 0.012).normalize().multiplyScalar(radius * 0.985));
    const inner = points.map(p => p.clone().lerp(center, 0.07).normalize().multiplyScalar(radius));
    const peak = normal.multiplyScalar(radius);
    for (let i = 0; i < points.length; i++) {
      const next = (i + 1) % points.length;
      triangle(peak, inner[i]!, inner[next]!, color);
      triangle(inner[i]!, outer[i]!, outer[next]!, seam);
      triangle(inner[i]!, outer[next]!, inner[next]!, seam);
    }
  };
  for (const face of faces) {
    const hex: THREE.Vector3[] = [];
    for (let i = 0; i < 3; i++) {
      const a = vertices[face[i]!]!, b = vertices[face[(i + 1) % 3]!]!;
      hex.push(a.clone().lerp(b, 1 / 3), a.clone().lerp(b, 2 / 3));
    }
    panel(hex, pale);
  }
  vertices.forEach((vertex, index) => {
    const neighbors = new Set<number>();
    for (const face of faces) if (face.includes(index)) for (const other of face) if (other !== index) neighbors.add(other);
    panel([...neighbors].map(other => vertex.clone().lerp(vertices[other]!, 1 / 3)), black);
  });
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
  const normals: number[] = [];
  for (let i = 0; i < positions.length; i += 3) {
    const length = Math.hypot(positions[i]!, positions[i + 1]!, positions[i + 2]!);
    normals.push(positions[i]! / length, positions[i + 1]! / length, positions[i + 2]! / length);
  }
  geometry.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
  return geometry;
}
